import React, { useState, useEffect, useRef, ReactElement, KeyboardEvent, useMemo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { AnyAction } from 'redux';
import { bindActionCreators } from 'redux';
import { useHistory } from 'react-router-dom';
import _ from 'lodash';
import { actions as projectMgrDashboardActions } from '@Actions/projectMgrDashboardActions';
import { actions as projectActions } from '@Actions/projectActions';
import { actions as newProjectWizardActions } from '@Actions/newProjectWizardActions';
import { actions as filesActions } from '@Actions/filesActions';
import { actions as baselineActions } from '@Actions/baselineActions';
import { actions as baselineTaskActions } from '@Actions/baselineTaskActions';
import { DataTable, DataTableColReorderEvent } from 'primereact/datatable';
import { Paginator } from 'primereact/paginator';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import Moment from 'moment';
import { DEFAULT_PAGE_SIZE, DATE_FORMAT, DATETIME_FORMAT, PROJECT_TASKS_LIST_UPDATE_OPTIONS as BulkOptions } from '../../../../constants/constants';
import { getNullableOnChangeValue } from '../../../../helpers/multiselect';
import * as types from '@Constants/actionTypes';
import Notification from '@Common/Notification';
import Modal61 from '@Common/Modal/Modal61';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { RadioButton } from 'primereact/radiobutton';
import {
    SelectAllCheck,
    DropdownBaselineTabs,
    ButtonBaselineTabs,
    BaselineTaskNameFilter,
    HeaderButtons,
    RowGroupHeaderButtons,
    ModalSubtitleLayout,
    ModalTitleLayout,
    RadioButtonOptions,
} from './styles';
import CreateTaskForm from '@Components/NewProjectWizard/ContentWidgets/Tasks/CreateTaskForm';
import { Task } from '@Components/NewProjectWizard/Interfaces/Types';
import { FormFilterResponse } from '@Components/NewProjectWizard/Interfaces/PropTypes';
import { Project } from '@Types/common';
import { CreateBaseline, CopyBaselineFromProject, ImportTasksWizzard } from './components/index';
import { daysToDaysWeeks } from '../../../../helpers/dateTime';
import { TruncateText } from '../../../../helpers/strings';
import Icon from '../../../Common/Icon';
import Tag from '../../../Common/Tag/Tag';
import MultiSelectBox from '../../../Common/MultiSelectBox/MultiSelectBox';
import BaselineTaskListColumnDrawer from '../../../BaselineTaskList/BaselineTaskListColumnDrawer';
import Routes from '@Constants/routes';
import { getDataTableSortFields } from '../../../../helpers/sortHelper';
import BaselinesForm from '../../../Subscription/Admin/BaselinesForm';
import OrderTasksDrawer from '@Components/BaselineTaskList/OrderTasksDrawer';
import BulkUpdateAttributesDrawer from '@Components/BaselineTaskList/BulkUpdate/BulkUpdateAttributesDrawer';
import BulkPushdownDrawer from './components/BulkPushdownDrawer';
import { ThunkDispatch } from 'redux-thunk';

type BulkActionSelectionTask = {
    baselineId: number;
    targetDaysOut?: number | null;
    targetStaticDate?: string | null;
};

export const getBulkPushdownValidationError = (selectedBaselineTasks: BulkActionSelectionTask[]) => {
    if (selectedBaselineTasks.length === 0) {
        return 'Please select at least one item to apply this action.';
    }

    const firstBaselineId = selectedBaselineTasks[0].baselineId;
    const isSameBaseline = selectedBaselineTasks.every((task) => task.baselineId === firstBaselineId);
    if (!isSameBaseline) {
        return 'To use the Bulk Pushdown feature, please select tasks from only one baseline.';
    }

    return null;
};

export const getOrderTasksValidationError = (selectedBaselineTasks: BulkActionSelectionTask[]) => {
    if (selectedBaselineTasks.length === 0) {
        return 'Please select at least one item to apply this action.';
    }

    const firstBaselineId = selectedBaselineTasks[0].baselineId;
    const targetDaysOut = selectedBaselineTasks[0].targetDaysOut;
    const targetStaticDate = selectedBaselineTasks[0].targetStaticDate;

    const isSameBaseline = selectedBaselineTasks.every((task) => task.baselineId === firstBaselineId);
    if (!isSameBaseline) {
        return 'To set the task order, please select tasks in only one baseline.';
    }

    const isSameTargetDate = selectedBaselineTasks.every(
        (task) => task.targetDaysOut === targetDaysOut && task.targetStaticDate === targetStaticDate
    );

    if (!isSameTargetDate) {
        return 'To use the Order Tasks feature, the selected tasks must be on the same Weeks/Days Out. Please reselect one or more tasks.';
    }

    return null;
};

const defaultFormData = {
    id: 0,
    name: '',
    description: '',
    contingentId: 0,
    phaseId: 0,
    departmentId: 0,
    communityId: 0,
    jobTitleId: 0,
    contingentName: '',
    contactName: '',
    contactEmail: '',
    additionalInfo: '',
    taskTagIds: [] as number[],
    isActive: true,
    isLocationTaskListActive: true,
    isLocationTaskDateAdjustable: true as boolean | null,
    links: [] as any[],
    targetDaysOut: null as number | null,
    targetDays: null as number | null,
    targetWeeks: null as number | null,
    targetStaticDate: null as string | null,
    startDaysOut: null as number | null,
    startDays: null as number | null,
    startWeeks: null as number | null,
    startStaticDate: null as string | null,
    anticipatedEffort: '',
    steps: [] as any[],
    prereqTasks: [] as any[],
    workflows: [] as any[],
    showStaticDates: false, // only for ui
    createdByName: '', // Only display if Editing
    createdDate: '', // Only display if Editing
    modifiedByName: '', // Only display if Editing
    modifiedDate: '', // Only display if Editing
    isMilestone: false,
    includeCertificationCheckbox: false,
    baselineIdsSelected: [] as any[],
};

interface ProjectTasksProps {
    projectData: Project;
    subscriptionId: number;
    userColumns: Array<string>;
    onColumnsChange: (newColumns: Array<string>) => Promise<void>;
}

interface SearchParams {
    projectKey: string;
    baselines: Array<number>;
    baselineTaskName: string;
    isActive: boolean;
    additionalInfo: string;
    departments: Array<number>;
    baselineJobTitles: Array<number>;
    communities: Array<number>;
    phases: Array<number>;
    disciplines: Array<number>;
    tags: Array<number>;
    createdDateStart: Date | null;
    createdDateEnd: Date | null;
    pageSize: number;
    pageNumber: number;
    sortBy: string;
    sortDir: string;
    multiColumnSort: Array<{ [key: string]: string }>;
}

interface BaselineTaskColumn {
    field: string;
    header: string;
    isFeatureEnabled: boolean;
    default: boolean;
    isSortable: boolean;
    body?(rowData: any): React.JSX.Element | React.JSX.Element[] | string | null;
    style?: React.CSSProperties;
}

interface DatePickerRange {
    rangeName: string;
    start: Date | null;
    end: Date | null;
}

interface ModalRefProps {
    hide: () => void;
    show: () => void;
}

interface BaselineTaskInactivateModalProps {
    baselineTaskIds: Array<number>;
    isActive: boolean;
    isLocationTaskListActive: boolean;
}

const bulkOptions = [
    { id: BulkOptions.UpdateAttributes.value, name: BulkOptions.UpdateAttributes.label },
    { id: BulkOptions.BulkPushdown.value, name: BulkOptions.BulkPushdown.label },
    { id: BulkOptions.PreviewPushdown.value, name: BulkOptions.PreviewPushdown.label },
    { id: BulkOptions.OrderTasks.value, name: BulkOptions.OrderTasks.label },
    { id: BulkOptions.Inactivate.value, name: BulkOptions.Inactivate.label },
];

const ProjectTasks = ({ projectData, subscriptionId, userColumns, onColumnsChange }: ProjectTasksProps): ReactElement => {
    const [baselines, setBaselines] = useState<Array<any>>([]);
    const [taskFormMode, setTaskFormMode] = useState<string>('create');
    const [expandedRows, setExpandedRows] = useState<any>(null);
    const [expandedBaselineTasks, setExpandedBaselineTasks] = useState<Map<number, { tasks: Array<any>; loading: boolean; loadingMore: boolean; page: number; totalCount: number }>>(new Map());
    const [loading, setLoading] = useState<number>(0);
    const [selectedBaselineTasks, setSelectedBaselineTasks] = useState<Array<any>>([]);
    const [baselineId, setBaselineId] = useState<number>(0);
    const [isColumnDrawerOpen, setIsColumnDrawerOpen] = useState<boolean>(false);
    const [isOrderTasksDrawerOpen, setIsOrderTasksDrawerOpen] = useState<boolean>(false);
    const [isBulkUpdateAttributesDrawerOpen, setIsBulkUpdateAttributesDrawerOpen] = useState<boolean>(false);
    const [isBulkPushdownDrawerOpen, setIsBulkPushdownDrawerOpen] = useState<boolean>(false);
    const [orderTasksTargetDaysOut, setOrderTasksTargetDaysOut] = useState<string>('');
    const [orderTasksTargetStaticDate, setOrderTasksTargetStaticDate] = useState<string>('');

    const [isCreateTaskDrawerOpen, setIsCreateTaskDrawerOpen] = useState<boolean>(false);
    const [isBaselineDrawerOpen, setIsBaselineDrawerOpen] = useState<boolean>(false);
    const [formFiltersLoaded, setFormFiltersLoaded] = useState<boolean>(false);
    const [columns, setColumns] = useState<Array<any>>([]);
    const [columnOptions, setColumnOptions] = useState<Array<any>>([]);
    const [datePickerRange, setDatePickerRange] = useState<DatePickerRange>({
        rangeName: '',
        start: null,
        end: null,
    });
    const [baselineTaskInactivateModal, setBaselineTaskInactivateModal] = useState<BaselineTaskInactivateModalProps>({
        baselineTaskIds: [],
        isActive: false,
        isLocationTaskListActive: false,
    });

    // Local state for radio button selections to improve performance
    const [localRadioState, setLocalRadioState] = useState({
        shouldRemoveFromBaseline: true, // true = Yes, false = No - Default to Yes (most common)
        shouldRemoveFromLocations: true, // true = Yes, false = No - Default to Yes (most common)
    });

    const [searchParams, setSearchParams] = useState<SearchParams>({
        projectKey: projectData.projectKey,
        baselines: [],
        baselineTaskName: '',
        isActive: true,
        pageSize: DEFAULT_PAGE_SIZE,
        pageNumber: 1,
        sortBy: '',
        sortDir: '',
        multiColumnSort: [
            {
                sortBy: 'baselineName',
                sortDir: 'asc',
            },
            {
                sortBy: 'Ordinal',
                sortDir: 'asc',
            },
            {
                sortBy: 'baselineTaskName',
                sortDir: 'asc',
            },
        ],
        additionalInfo: '',
        departments: [],
        baselineJobTitles: [],
        communities: [],
        phases: [],
        disciplines: [],
        tags: [],
        createdDateStart: null,
        createdDateEnd: null,
    });

    const { sortField, sortOrder } = searchParams.multiColumnSort.length
        ? getDataTableSortFields(searchParams.multiColumnSort.find(({ sortBy }) => sortBy === 'baselineName'))
        : getDataTableSortFields(searchParams);

    const [totalCount, setTotalCount] = useState<number>(0);
    const [availableBulkOperations] = useState(bulkOptions);
    const [selectedBulkOperation, setSelectedBulkOperation] = useState(BulkOptions.BulkUpdate.value);

    const activeOptions = [
        { value: 1, label: 'Import Tasks', icon: 'fa-file-import' },
        { value: 2, label: 'Add Task', icon: 'fa-share-from-square' },
        { value: 3, label: 'Copy Tasks from other Project', icon: 'fa-files' },
        { value: 4, label: 'New Baseline', icon: 'fa-circle-plus' },
    ];

    const optionTemplate = (option: any) => {
        const iconName = `fa-regular ${option.icon} me-2`;
        return (
            <div>
                <i className={iconName} /> {option.label}
            </div>
        );
    };

    const [formData, setFormData] = useState<Task>({
        id: 0,
        name: '',
        description: '',
        contingentId: 0,
        phaseId: '',
        departmentId: '',
        communityId: '',
        jobTitleId: '',
        contingentName: '',
        baselineId: 0,
        contactName: '',
        contactEmail: '',
        additionalInfo: '',
        taskTagIds: [],
        isActive: true,
        isLocationTaskListActive: true,
        isLocationTaskDateAdjustable: true,
        links: [],
        targetDaysOut: null,
        targetDays: null,
        targetWeeks: null,
        targetStaticDate: null,
        startDaysOut: null,
        startDays: null,
        startWeeks: null,
        startStaticDate: null,
        anticipatedEffort: '',
        steps: [],
        prereqTasks: [],
        showStaticDates: false,
        isMilestone: false,
        createdByName: '',
        createdDate: '',
        modifiedByName: '',
        modifiedDate: '',
        includeCertificationCheckbox: false,
        baselineIdsSelected: [],
        workflowEventId: null,
        workflows: [],
    });

    const [formFilters, setFormFilters] = useState<FormFilterResponse>({
        departments: [],
        subscriptionDepartments: [],
        baselineJobTitles: [],
        communities: [],
        phases: [],
        disciplines: [],
        certifiedTaskId: 0,
        subscriptionFiles: [],
        taskTags: [],
        isLoaded: false,
        isStartDateActive: false,
        isTaskStepsActive: false,
        isTaskEffortActive: false,
        isTaskFeedbackActive: false,
        isTaskDependenciesActive: false,
        isProjectWidgetActive: false,
        latestBaselineId: 0,
        lowestCommunityId: 0,
        baselineIdsFilter: [],
    });

    const [currentFormState, setCurrentFormState] = useState({
        editTask: null as Task | null,
        unchangedTask: null as Task | null,
        newTask: null as Task | null,
        isFormValid: true,
        showDateError: false,
        isLoading: false,
        taskSearchResults: [],
        taskPrereqSearchResults: [],
        unsavedChanges: false,
        saveAndNewRetention: false,
        baselineId: baselineId,
    });

    const dispatch: ThunkDispatch<any, any, AnyAction> = useDispatch();
    const getFormFilters = async (baselineIdParam: number) => dispatch(newProjectWizardActions.loadFormFilters(subscriptionId, baselineIdParam));
    const saveBaselineTaskNewWizard = async (task: object) => dispatch(newProjectWizardActions.saveBaselineTask(task));
    const searchProjectBaselinesV2 = async (params: any) => dispatch(projectMgrDashboardActions.searchProjectBaselinesV2(params));
    const unlinkBaseline = async (params: any) => dispatch(projectMgrDashboardActions.unlinkBaseline(params));
    const listProjectBaselines = async (params: any) => dispatch(projectMgrDashboardActions.listProjectBaselines(params));
    // Source for the "Baselines" field options. Deliberately NOT the paged
    // projectmgr-dashboard search above: that route is driven from BaselineTask (so a new,
    // still-empty baseline never appears) and defaults to PageSize 10 (so on projects with
    // more than 10 baselines the rest are dropped). This route returns every baseline the
    // user can access on the project, unpaged.
    const listAllProjectBaselines = async (projectId: number) => dispatch(projectActions.listProjectBaselines(projectId));
    const downloadFile = async (fileName: string) => dispatch(filesActions.download(fileName));
    const baselineTasksActiveToggle = async (params: any) =>
        dispatch(baselineTaskActions.activeToggle(params.baselineTaskIds, params.isActive, params.isLocationTaskListActive));
    const getBaselineTask = async (id: number) => dispatch(baselineTaskActions.loadBaselineTask(id));
    // The new-project-wizard form-filters endpoint does not return workflow options,
    // so pull them from the existing baseline-task form-filters endpoint and merge them in.
    const getWorkflowOptions = async (bid: number) => dispatch(baselineTaskActions.loadBaselineTaskFormFilters(subscriptionId, bid));

    const baselineActionCreators = useMemo(() => bindActionCreators(baselineActions, dispatch), [dispatch]);
    const baselineTaskActionCreators = useMemo(() => bindActionCreators(baselineTaskActions, dispatch), [dispatch]);

    const history = useHistory();

    // Define function to avoid editor issue
    const modalBodyRef = useRef<{ createBaseline: () => Promise<void>; copyBaselines: () => Promise<void> } | null>(null);

    const importTasksModalRef = useRef<ModalRefProps | null>(null);
    const copyBaselineModalRef = useRef<ModalRefProps | null>(null);
    const newBaselineModalRef = useRef<ModalRefProps | null>(null);
    const removeBaselineModalRef = useRef<ModalRefProps | null>(null);
    const inactivateBaselineTasks = useRef<ModalRefProps | null>(null);
    const loadMoreInFlight = useRef<Set<number>>(new Set());

    // This routine is a little oddly named as it originally was used only to fetch form filter data
    // But it also fetches feature toggles used in this page
    // Memoize loadFormFilters to prevent unnecessary calls
    const loadFormFilters = useCallback(
        async (overrideFiltersLoaded: boolean = false) => {
            if (formFiltersLoaded && !overrideFiltersLoaded) return;

            setLoading((prevLoading) => prevLoading + 1);

            setCurrentFormState((prevState) => ({
                ...prevState,
                isLoading: true,
            }));

            const currentProjectBaselines = await listProjectBaselinesAsync();
            const firstBaselineId = currentProjectBaselines[0]?.id;

            setBaselineId(firstBaselineId);
            updateBaselineId(firstBaselineId);

            try {
                const result = await getFormFilters(firstBaselineId);
                const payload = result.payload;
                if (result.type === types.LOAD_NEW_PROJECT_FORM_FILTERS_SUCCESS) {
                    setFormFilters((prevState) => ({
                        ...prevState,
                        ...payload,
                        baselineIdsFilter: currentProjectBaselines,
                    }));

                    // Merge in workflow options (not provided by the wizard form-filters endpoint)
                    try {
                        const wfResult = await getWorkflowOptions(firstBaselineId);
                        if (wfResult.type === types.LOAD_BASELINE_TASK_FORM_FILTERS_SUCCESS && wfResult.payload?.workflows) {
                            setFormFilters((prevState) => ({
                                ...prevState,
                                workflows: wfResult.payload.workflows,
                            }));
                        }
                    } catch {
                        // non-fatal: workflow options just won't be available
                    }

                    if (currentProjectBaselines.length === 1) {
                        const newBaselineId = firstBaselineId;

                        if (baselineId === 0) {
                            setBaselineId(newBaselineId);
                            updateBaselineId(newBaselineId);
                        }

                        setCurrentFormState((prevState) => ({
                            ...prevState,
                            baselineId: newBaselineId,
                            communityId: payload.lowestCommunityId,
                        }));

                        setFormData((prevData) => ({
                            ...prevData,
                            baselineId: newBaselineId,
                            communityId: payload.lowestCommunityId,
                            baselineIdsSelected: currentProjectBaselines.map((item) => item.id),
                        }));
                    } else if (currentProjectBaselines.length > 1 && payload.latestBaselineId > 0) {
                        const newBaselineId = baselineId === 0 ? payload.latestBaselineId + 1 : baselineId;

                        if (baselineId === 0) {
                            setBaselineId(newBaselineId);
                            updateBaselineId(newBaselineId);
                        }

                        setCurrentFormState((prevState) => ({
                            ...prevState,
                            baselineId: newBaselineId,
                            communityId: payload.lowestCommunityId,
                        }));

                        setFormData((prevData) => ({
                            ...prevData,
                            baselineId: newBaselineId,
                            communityId: payload.lowestCommunityId,
                            baselineIdsSelected: prevData.baselineIdsSelected,
                        }));
                    }

                    setFormFiltersLoaded(true);
                } else if (result.type === types.LOAD_NEW_PROJECT_FORM_FILTERS_FAILURE) {
                    Notification('error', 'An Error Occurred', payload.message);
                }
            } catch (error) {
                Notification('error', 'Error', error);
            } finally {
                setCurrentFormState((prevState) => ({
                    ...prevState,
                    isLoading: false,
                }));
                setLoading((prevLoading) => prevLoading - 1);
            }
        },
        [formFiltersLoaded, projectData.id, baselineId]
    );

    // Memoize onFormChange to prevent recreation on every render
    const onFormChange = useCallback((name, value) => {
        setFormData((prevData) => {
            const updatedFormData = {
                ...prevData,
                [name]: value
            };
            // Update currentFormState directly within this callback to ensure it has the latest formData
            setCurrentFormState((prevState) => ({
                ...prevState,
                editTask: updatedFormData,
                unchangedTask: updatedFormData,
                newTask: updatedFormData,
            }));

            return updatedFormData;
        });
    }, []);

    const validateForm = (task) => {
        const { name, jobTitleId, departmentId, communityId } = task;

        // if there are steps, make sure they all have a name
        for (let i = 0; i < task.steps?.length; i++) {
            if (task.steps[i].name == '') {
                return false;
            }
        }

        // A partially-filled link must be completed before saving. Fully blank rows are
        // placeholder rows and get dropped in onSave; silently dropping a link with only
        // one field filled would delete it on the server.
        for (let i = 0; i < task.links?.length; i++) {
            const link = task.links[i];
            const hasUrl = (link.url ?? '').trim().length > 0;
            const hasName = (link.name ?? '').trim().length > 0;
            if (hasUrl && !hasName) {
                // if there are links, make sure they all have abbreviations
                // if there is white space clear it out so element validation is visible
                link.name = '';
                const links = task.links.slice();
                links.splice(i, 1, link);
                //#TODO FIX THIS
                onFormChange('links', links);
                return false;
            }
            if (hasName && !hasUrl) {
                Notification('error', 'Link URL Required', `The link "${(link.name ?? '').trim()}" is missing a URL. Add a URL or remove the link.`);
                return false;
            }
        }

        if (currentFormState.editTask?.showStaticDates && !currentFormState.editTask.targetStaticDate) {
            return false;
        }

        if (
            currentFormState.editTask?.showStaticDates === false &&
            (currentFormState.editTask.targetWeeks === null || currentFormState.editTask.targetWeeks === undefined)
        ) {
            return false;
        }

        // Validate that at least one of targetWeeks or targetDays is non-zero
        // Validate that targetDays is provided when using relative dates
        if (
            currentFormState.editTask?.showStaticDates === false &&
            (currentFormState.editTask.targetDays === null || currentFormState.editTask.targetDays === undefined)
        ) {
            return false;
        }
        
        const isJobTitleValid =
            jobTitleId &&
            (formFilters.baselineJobTitles ?? []).some((jt: any) => jt.id?.toString() === jobTitleId?.toString());

        if (name && name.trim().length > 0 && isJobTitleValid && departmentId && communityId) {
            return true;
        }

        return false;
    };

    const onSave = async (saveDataAndRetention) => {
        if (currentFormState.isLoading) {
            return;
        }

        setCurrentFormState({
            ...currentFormState,
            isLoading: true,
        });

        const task = {
            ...currentFormState.editTask,
            baselineId: currentFormState.editTask?.baselineId || baselineId,
            projectKey: searchParams.projectKey,
        };

        if (saveDataAndRetention) {
            setCurrentFormState({
                ...currentFormState,
                saveAndNewRetention: !currentFormState.saveAndNewRetention,
                showDateError: false,
            });
        }

        if (!validateForm(task)) {
            setCurrentFormState((prevState) => ({
                ...prevState,
                isFormValid: false,
                isLoading: false,
            }));
            return;
        }

        if (currentFormState.editTask?.showStaticDates) {
            // clear out any values in the other section that isn't visible so it does transport to api
            task.targetDaysOut = null;
            task.startDaysOut = null;
            task.targetDays = null;
            task.targetWeeks = null;
            task.startDays = null;
            task.startWeeks = null;
            task.isLocationTaskDateAdjustable = false;

            // validate start before target based on the section that is showing (BUT ONLY IF ISSTARTDATEACTIVE)
            if (formFilters.isStartDateActive) {
                if (task.startStaticDate !== undefined || task.targetStaticDate !== undefined) {
                    const start = task.startStaticDate ? new Date(task.startStaticDate) : null;
                    const target = task.targetStaticDate ? new Date(task.targetStaticDate) : null;

                    // NOTE: you can start and finish something the same day
                    if (start && target && target.getTime() < start.getTime()) {
                        setCurrentFormState((prevState) => ({
                            ...prevState,
                            showDateError: true,
                            isFormValid: false,
                            isLoading: false,
                        }));
                        return;
                    } else {
                        setCurrentFormState((prevState) => ({
                            ...prevState,
                            showDateError: false,
                        }));
                    }
                }
            }
        } else {
            // days and weeks
            const targetWeeks = task.targetWeeks === null ? 0 : parseInt((task.targetWeeks ?? 0).toString());
            const targetDays = task.targetDays === null ? 0 : parseInt((task.targetDays ?? 0).toString());
            const startWeeks = task.startWeeks === null ? 0 : parseInt((task.startWeeks ?? 0).toString());
            const startDays = task.startDays === null ? 0 : parseInt((task.startDays ?? 0).toString());

            // convert target/start dates to total days (values can be negative!)
            task.targetDaysOut = targetWeeks * 7 + targetDays;
            task.startDaysOut = startWeeks * 7 + startDays;

            task.startStaticDate = null;
            task.targetStaticDate = null;

            // Relative-date tasks must send a non-null adjustable flag (server rejects null).
            // Default mirrors BaselineTaskDrawer: locked when the project widget is active.
            task.isLocationTaskDateAdjustable = formFilters.isProjectWidgetActive ? false : true;

            // validate start before target based on the section that is showing (BUT ONLY IF ISSTARTDATEACTIVE)
            if (formFilters.isStartDateActive && task.startDaysOut > 0) {
                if (task.targetDaysOut > task.startDaysOut) {
                    setCurrentFormState((prevState) => ({
                        ...prevState,
                        showDateError: true,
                        isFormValid: false,
                        isLoading: false,
                    }));
                    return;
                } else {
                    setCurrentFormState((prevState) => ({
                        ...prevState,
                        showDateError: false,
                    }));
                }
            } else {
                task.startDaysOut = null;
            }
        }

        // if IsActive make sure isLocationTaskListActive is true
        if (task.isActive) {
            task.isLocationTaskListActive = true;
        }

        // Remove only fully blank placeholder rows. A link with either field filled is kept:
        // the server treats a missing link as "deleted" and deactivates it everywhere, so
        // partially-filled links must be blocked by validateForm, never silently dropped here.
        if (task.links && task.links.length) {
            task.links = task.links.filter((x) =>
                (x.name && x.name.trim().length) || (x.url && x.url.trim().length));
        }

        //transforms taskTagIds from string to array of strings
        if (task.taskTagIds && task.taskTagIds.length > 0) {
            if (!Array.isArray(task.taskTagIds)) {
                task.taskTagIds = String(task.taskTagIds).split(',').map(id => parseInt(id.trim(), 10)).filter(id => !isNaN(id));
            } else {
                task.taskTagIds = task.taskTagIds.map(id => typeof id === 'string' ? parseInt(id, 10) : id).filter(id => !isNaN(id));
            }
        }

        setCurrentFormState((prevState) => ({
            ...prevState,
            isFormValid: true,
            isLoading: true,
        }));

        try {
            if (typeof (task as any).baselineIdsSelected === 'string' && (task as any).baselineIdsSelected.length > 0) {
                const stringArray = String((task as any).baselineIdsSelected)
                    .split(',')
                    .map((id) => Number(id.trim()))
                    .filter((id) => !isNaN(id));
                (task as any).baselineIdsSelected = stringArray;
            } else if (!Array.isArray((task as any).baselineIdsSelected)) {
                (task as any).baselineIdsSelected = [];
            }

            const response = await saveBaselineTaskNewWizard(task);
            if (response.type === types.NEW_PROJECT_WIZARD_CREATE_BASELINE_TASK_SUCCESS || response.type === types.UPDATE_BASELINE_TASK_SUCCESS) {
                Notification(
                    'success',
                    'Success',
                    response.type === types.NEW_PROJECT_WIZARD_CREATE_BASELINE_TASK_SUCCESS ? 'Baseline Task Created' : 'Baseline Task Updated'
                );
                const currentBaselineId = currentFormState.editTask?.baselineId;
                if (saveDataAndRetention) {
                    // retain these properties and keep the drawer open
                    const retainProps = {
                        isMilestone: currentFormState.editTask?.isMilestone,
                        jobTitleId: currentFormState.editTask?.jobTitleId,
                        departmentId: currentFormState.editTask?.departmentId,
                        communityId: currentFormState.editTask?.communityId,
                        phaseId: currentFormState.editTask?.phaseId,
                        baselineId: currentBaselineId,
                        isActive: currentFormState.editTask?.isActive,
                        baselineIdsSelected: currentFormState.editTask?.baselineIdsSelected,
                    };

                    const newTask = {
                        ...defaultFormData,
                        ...retainProps,
                    };

                    if (response.payload.baselineId && response.payload.baselineId !== 0) updateBaselineId(response.payload.baselineId);

                    onDrawerComplete(currentBaselineId);

                    const mappedNewTask: Task = {
                        id: newTask.id || 0,
                        name: newTask.name || '',
                        description: newTask.description || '',
                        contingentId: newTask.contingentId || 0,
                        phaseId: newTask.phaseId || '',
                        departmentId: newTask.departmentId || '',
                        communityId: newTask.communityId || '',
                        jobTitleId: newTask.jobTitleId || '',
                        contingentName: newTask.contingentName || '',
                        baselineId: newTask.baselineId || 0,
                        contactName: newTask.contactName || '',
                        contactEmail: newTask.contactEmail || '',
                        additionalInfo: newTask.additionalInfo || '',
                        taskTagIds: Array.isArray(newTask.taskTagIds) ? newTask.taskTagIds : [],
                        isActive: newTask.isActive ?? true,
                        isLocationTaskListActive: newTask.isLocationTaskListActive ?? true,
                        isLocationTaskDateAdjustable: newTask.isLocationTaskDateAdjustable ?? true,
                        links: Array.isArray(newTask.links) ? newTask.links : [],
                        targetDaysOut: newTask.targetDaysOut ?? null,
                        targetDays: newTask.targetDays ?? null,
                        targetWeeks: newTask.targetWeeks ?? null,
                        targetStaticDate: newTask.targetStaticDate ?? null,
                        startDaysOut: newTask.startDaysOut ?? null,
                        startDays: newTask.startDays ?? null,
                        startWeeks: newTask.startWeeks ?? null,
                        startStaticDate: newTask.startStaticDate ?? null,
                        anticipatedEffort: newTask.anticipatedEffort || '',
                        steps: Array.isArray(newTask.steps) ? newTask.steps : [],
                        prereqTasks: Array.isArray(newTask.prereqTasks) ? newTask.prereqTasks : [],
                        showStaticDates: newTask.showStaticDates ?? false,
                        createdByName: newTask.createdByName || '',
                        createdDate: newTask.createdDate || '',
                        modifiedByName: newTask.modifiedByName || '',
                        modifiedDate: newTask.modifiedDate || '',
                        isMilestone: newTask.isMilestone ?? false,
                        includeCertificationCheckbox: newTask.includeCertificationCheckbox ?? false,
                        baselineIdsSelected: Array.isArray(newTask.baselineIdsSelected) ? newTask.baselineIdsSelected : []
                    };

                    setFormData(mappedNewTask);

                    setCurrentFormState((prevState) => ({
                        ...prevState,
                        editTask: mappedNewTask,
                        isLoading: false,
                        saveAndNewRetention: false,
                        baselineId: baselineId,
                        showDateError: false,
                    }));
                } else {
                    //We assign this to a new baseline. wILL BE LOADED IN FORM FILTERS
                    onDrawerComplete();
                    setCurrentFormState({
                        ...currentFormState,
                        editTask: formData,
                        unchangedTask: formData,
                        newTask: formData,
                    });

                    setIsCreateTaskDrawerOpen(false);
                }
            } else if (
                response.type === types.NEW_PROJECT_WIZARD_CREATE_BASELINE_TASK_FAILURE ||
                response.type === types.UPDATE_BASELINE_TASK_FAILURE
            ) {
                Notification('error', 'An Error Occurred', response.payload.message);
                setCurrentFormState((prevState) => ({
                    ...prevState,
                    isLoading: false,
                }));
            }
        } catch (error) {
            Notification('error', 'An Error Occurred', error);
            setCurrentFormState((prevState) => ({
                ...prevState,
                isLoading: false,
            }));
        } finally {
            setCurrentFormState((prevState) => ({
                ...prevState,
                isLoading: false,
            }));

            search(searchParams);
        }
    };

    const resetFormData = (baselineId: number) => {
        setFormData({
            id: 0,
            name: '',
            description: '',
            contingentId: 0,
            phaseId: '',
            departmentId: '',
            communityId: '',
            jobTitleId: '',
            contingentName: '',
            baselineId: baselineId,
            contactName: '',
            contactEmail: '',
            additionalInfo: '',
            taskTagIds: [],
            isActive: true,
            isLocationTaskListActive: true,
            isLocationTaskDateAdjustable: true,
            links: [],
            targetDaysOut: null,
            targetDays: null,
            targetWeeks: null,
            targetStaticDate: null,
            startDaysOut: null,
            startDays: null,
            startWeeks: null,
            startStaticDate: null,
            anticipatedEffort: '',
            steps: [],
            prereqTasks: [],
            workflows: [],
            showStaticDates: false,
            isMilestone: false,
            createdByName: '',
            createdDate: '',
            modifiedByName: '',
            modifiedDate: '',
            includeCertificationCheckbox: false,
            baselineIdsSelected: [],
            workflowEventId: null,
        });
    };

    const onDrawerComplete = (baselineId = 0) => {
        if (baselineId === 0) {
            setIsCreateTaskDrawerOpen(false);
        }
        resetFormData(0);

        setCurrentFormState({
            ...currentFormState,
            editTask: formData,
            unchangedTask: formData,
            newTask: formData,
        });
    };

    // Memoize checkForChanges to prevent recreation on every render
    const checkForChanges = useCallback(() => {
        // compare unChangedTask to editTask if editing a task
        if (currentFormState.editTask?.id != 0) {
            const haveTheSameValues = _.isEqual(currentFormState.unchangedTask, currentFormState.editTask);
            setCurrentFormState((prevState) => ({
                ...prevState,
                unsavedChanges: !haveTheSameValues,
            }));
        }
    }, [currentFormState.editTask, currentFormState.unchangedTask]);

    const updateBaselineId = (newBaselineId: number) => {
        setBaselineId(newBaselineId);
        setCurrentFormState((prevState) => ({
            ...prevState,
            baselineId: newBaselineId,
        }));
        setCurrentFormState((prevState) => ({
            ...prevState,
            baselineId: newBaselineId,
        }));
        setFormFilters((prevState) => ({
            ...prevState,
            latestBaselineId: newBaselineId,
        }));
    };

    const updateSearchParams = (name: string, value: any): boolean => {
        if (searchParams[name] != value) {
            setSearchParams({
                ...searchParams,
                [name]: value,
            });

            return true; // search params were changed
        }

        return false; // no change to search params
    };

    const search = async (params: SearchParams) => {
        setLoading((prevLoading) => prevLoading + 1);
        try {
            const result = await listProjectBaselines(params);
            const payload = result.payload;
            if (result.type === types.LIST_PROJECT_BASELINES_SUCCESS) {
                setSelectedBaselineTasks([]);
                setExpandedRows(null);
                setExpandedBaselineTasks(new Map());
                const items = (payload.results ?? payload.baselines ?? []).map((item: any) => ({
                    baselineId: item.baselineId ?? item.id,
                    baselineName: item.baselineName ?? item.name,
                    baselineTaskId: -((item.baselineId ?? item.id)),
                    _placeholder: true,
                }));
                setBaselines(items);
                setTotalCount(payload.totalCount ?? items.length);
            } else if (result.type === types.LIST_PROJECT_BASELINES_FAILURE) {
                Notification('error', 'An Error Occurred', payload.message);
            }
        } catch (error) {
            Notification('error', 'Error', error);
        } finally {
            setLoading((prevLoading) => prevLoading - 1);
        }
    };

    const loadTasksForBaseline = async (baselineId: number, baselineName: string) => {
        setExpandedBaselineTasks((prev) => {
            const next = new Map(prev);
            next.set(baselineId, { tasks: [], loading: true, loadingMore: false, page: 1, totalCount: 0 });
            return next;
        });
        try {
            const request = { ...searchParams, baselines: [baselineId], pageNumber: 1 };
            const result = await searchProjectBaselinesV2(request);
            const payload = result.payload;
            if (result.type === types.LOAD_PROJECT_BASELINES_SUCCESS_V2) {
                const tasks = (payload.results ?? []).map((t: any) => ({ ...t, baselineName, baselineId }));
                setExpandedBaselineTasks((prev) => {
                    const next = new Map(prev);
                    next.set(baselineId, { tasks, loading: false, loadingMore: false, page: 1, totalCount: payload.totalCount ?? tasks.length });
                    return next;
                });
                setBaselines((prev) => {
                    const updated: any[] = [];
                    for (const row of prev) {
                        if (row._placeholder && row.baselineId === baselineId) {
                            if (tasks.length > 0) {
                                updated.push(...tasks);
                            } else {
                                updated.push(row); // keep placeholder if no tasks
                            }
                        } else {
                            updated.push(row);
                        }
                    }
                    return updated;
                });
            } else {
                Notification('error', 'An Error Occurred', payload?.message);
                setExpandedBaselineTasks((prev) => {
                    const next = new Map(prev);
                    next.set(baselineId, { tasks: [], loading: false, loadingMore: false, page: 1, totalCount: 0 });
                    return next;
                });
            }
        } catch (error) {
            Notification('error', 'Error', error);
            setExpandedBaselineTasks((prev) => {
                const next = new Map(prev);
                next.set(baselineId, { tasks: [], loading: false, loadingMore: false, page: 1, totalCount: 0 });
                return next;
            });
        }
    };

    const loadMoreTasksForBaseline = async (baselineId: number, baselineName: string) => {
        const current = expandedBaselineTasks.get(baselineId);
        if (!current || current.loadingMore || current.loading || loadMoreInFlight.current.has(baselineId)) return;

        loadMoreInFlight.current.add(baselineId);
        const nextPage = current.page + 1;

        setExpandedBaselineTasks((prev) => {
            const next = new Map(prev);
            const entry = next.get(baselineId);
            if (entry) next.set(baselineId, { ...entry, loadingMore: true });
            return next;
        });

        try {
            const request = { ...searchParams, baselines: [baselineId], pageNumber: nextPage };
            const result = await searchProjectBaselinesV2(request);
            const payload = result.payload;
            if (result.type === types.LOAD_PROJECT_BASELINES_SUCCESS_V2) {
                const newTasks = (payload.results ?? []).map((t: any) => ({ ...t, baselineName, baselineId }));
                setExpandedBaselineTasks((prev) => {
                    const next = new Map(prev);
                    const entry = next.get(baselineId);
                    if (entry) {
                        next.set(baselineId, {
                            tasks: [...entry.tasks, ...newTasks],
                            loading: false,
                            loadingMore: false,
                            page: nextPage,
                            totalCount: payload.totalCount ?? entry.totalCount,
                        });
                    }
                    return next;
                });
                setBaselines((prev) => {
                    const updated = [...prev];
                    let lastIdx = -1;
                    for (let i = 0; i < updated.length; i++) {
                        if (updated[i].baselineId === baselineId && !updated[i]._placeholder) {
                            lastIdx = i;
                        }
                    }
                    if (lastIdx >= 0) {
                        updated.splice(lastIdx + 1, 0, ...newTasks);
                    } else {
                        updated.push(...newTasks);
                    }
                    return updated;
                });
            } else {
                Notification('error', 'An Error Occurred', payload?.message);
                setExpandedBaselineTasks((prev) => {
                    const next = new Map(prev);
                    const entry = next.get(baselineId);
                    if (entry) next.set(baselineId, { ...entry, loadingMore: false });
                    return next;
                });
            }
        } catch (error) {
            Notification('error', 'Error', error);
            setExpandedBaselineTasks((prev) => {
                const next = new Map(prev);
                const entry = next.get(baselineId);
                if (entry) next.set(baselineId, { ...entry, loadingMore: false });
                return next;
            });
        } finally {
            loadMoreInFlight.current.delete(baselineId);
        }
    };

    const handleRowToggle = (e: { data: any }) => {
        const newExpandedRows = e.data;
        setExpandedRows(newExpandedRows);

        if (!newExpandedRows || !newExpandedRows.length) return;

        newExpandedRows.forEach((expandedRow: any) => {
            const matchedBaseline = typeof expandedRow === 'object' && expandedRow !== null
                ? baselines.find((baseline) =>
                    baseline.baselineId === expandedRow.baselineId ||
                    baseline.baselineName === expandedRow.baselineName,
                ) || expandedRow
                : baselines.find((baseline) =>
                    baseline.baselineId === expandedRow ||
                    baseline.baselineName === expandedRow,
                );

            const baselineId = matchedBaseline?.baselineId;
            const baselineName = matchedBaseline?.baselineName;

            if (baselineId && baselineName && !expandedBaselineTasks.has(baselineId)) {
                loadTasksForBaseline(baselineId, baselineName);
            }
        });
    };

    const onColumnsClick = () => {
        setIsColumnDrawerOpen(true);
    };

    const onDrawerClose = () => {
        setIsColumnDrawerOpen(false);
        setIsBaselineDrawerOpen(false);
    };

    const onColumnToggle = async (name, value) => {
        const cols = columns.slice();
        const modifiedColumn = columnOptions.find((m) => m.value.field === name);

        if (value) {
            cols.push(modifiedColumn.value);
        } else {
            cols.splice(columns.indexOf(modifiedColumn.value), 1);
        }

        setColumns(cols);

        // Save columns personalization
        await onColumnsChange(cols.map((c) => c.field));
    };

    const onColumnReorder = async (e: DataTableColReorderEvent) => {
        // Save columns personalization
        const newColumns: Array<string | undefined> = e.columns.map((c) => c.props.field);
        await onColumnsChange(newColumns.filter((col): col is string => typeof col === 'string'));
    };

    const selectBulkOperation = async () => {
        if (selectedBaselineTasks.length === 0) {
            Notification('error', 'Error', 'Please select at least one item to apply this action.');
            return;
        }

        switch (selectedBulkOperation) {
            case BulkOptions.UpdateAttributes.value:
                openUpdateAttributesDrawer();
                break;
            case BulkOptions.BulkPushdown.value:
                openBulkPushdownDrawer();
                break;
            case BulkOptions.PreviewPushdown.value:
                break;
            case BulkOptions.OrderTasks.value:
                openOrderTasksDrawer();
                break;
            case BulkOptions.Inactivate.value:
                showInactivateModal();
                break;
            default:
                break;
        }
    };

    const openUpdateAttributesDrawer = () => {
        setIsBulkUpdateAttributesDrawerOpen(true);
    };

    const openBulkPushdownDrawer = () => {
        const validationError = getBulkPushdownValidationError(selectedBaselineTasks);
        if (validationError) {
            Notification('error', 'Error', validationError);
            return;
        }

        const firstBaselineId = selectedBaselineTasks[0].baselineId;
        setBaselineId(firstBaselineId);
        setIsBulkPushdownDrawerOpen(true);
    };

    const openOrderTasksDrawer = () => {
        const validationError = getOrderTasksValidationError(selectedBaselineTasks);
        if (validationError) {
            Notification('error', 'Error', validationError);
            return;
        }

        const firstBaselineId = selectedBaselineTasks[0].baselineId;
        const targetDaysOut = selectedBaselineTasks[0].targetDaysOut;
        const targetStaticDate = selectedBaselineTasks[0].targetStaticDate;

        setBaselineId(firstBaselineId);
        setOrderTasksTargetDaysOut(targetDaysOut?.toString() || '');
        setOrderTasksTargetStaticDate(targetStaticDate || '');
        setIsOrderTasksDrawerOpen(true);
    };

    // Function to handle "Select All" checkbox click
    const toggleSelectAll = useCallback(() => {
        const allTasks: Array<any> = [];
        expandedBaselineTasks.forEach(({ tasks }) => allTasks.push(...tasks));
        if (selectedBaselineTasks.length === allTasks.length && allTasks.length > 0) {
            setSelectedBaselineTasks([]);
        } else {
            setSelectedBaselineTasks([...allTasks]);
        }
    }, [expandedBaselineTasks, selectedBaselineTasks]);

    // Handler for DataTable selection changes
    const onSelectionChange = useCallback((e) => {
        setSelectedBaselineTasks(e.value.filter((t: any) => !t._placeholder));
    }, []);

    const SelectAllCheckbox = () => {
        const allTasks: Array<any> = [];
        expandedBaselineTasks.forEach(({ tasks }) => allTasks.push(...tasks));
        const isAllSelected = selectedBaselineTasks.length === allTasks.length && allTasks.length > 0;

        return <Checkbox checked={isAllSelected} onChange={toggleSelectAll} />;
    };

    const headerComponent = (
        <div className='row'>
            <div className='col-md-6'>
                <HeaderButtons>
                    <SelectAllCheck>
                        <SelectAllCheckbox />
                        <span>Select All</span>
                    </SelectAllCheck>
                    <MultiSelectBox
                        name='views'
                        idField='id'
                        valueField='name'
                        placeholder='Select Bulk Action'
                        className={'multi-select-bulk'}
                        options={availableBulkOperations}
                        onChange={(e: string) => setSelectedBulkOperation(e)}
                        selectedValues={selectedBulkOperation}
                        isMulti={false}
                        isSearchable={false}
                    />
                    <Button label='Apply' onClick={selectBulkOperation} className='pacer-table-button' />
                </HeaderButtons>
            </div>
            <div className='col-md-6'>
                <div className='d-flex justify-content-end'>
                    <Button icon='fa-regular fa-columns-3 fa-xl' title='Choose Columns' onClick={onColumnsClick} className='pacer-table-button' />
                </div>
            </div>
        </div>
    );

    // Checkbox template removed - using native DataTable selection

    const onPageChange = async (e) => {
        const updatedParams = {
            ...searchParams,
            pageNumber: e.page + 1,
            pageSize: e.rows,
        };

        setSearchParams(updatedParams);
    };

    const keyUpFilter = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            const target = event.target as HTMLInputElement;
            updateSearchParams(target.name, target.value);
        }
    };

    const listProjectBaselinesAsync = async () => {
        setLoading((prevLoading) => prevLoading + 1);
        try {
            const result = await listAllProjectBaselines(projectData.id);

            if (result.type === types.LIST_PROJECT_BASELINES_SUCCESS) {
                const currentBaselineIds = (result.payload.baselines ?? result.payload.results ?? []).map((item) => ({
                    id: item.baselineId ?? item.id,
                    name: item.baselineName ?? item.name,
                }));

                if (currentBaselineIds.length === 1) {
                    setFormData({ ...formData, baselineIdsSelected: currentBaselineIds.map((item) => item.id) });
                }

                // Return the baselines at the end of the success block
                return currentBaselineIds;
            } else if (result.type === types.LIST_PROJECT_BASELINES_FAILURE) {
                Notification('error', 'An Error Occurred', result.payload.message);
                return [];
            }
        } catch (error) {
            Notification('error', 'Error', error);
            return [];
        } finally {
            setLoading((prevLoading) => prevLoading - 1);
        }
    };

    const onExpandTasks = (baselineId: number) => {
        history.push(Routes.BaselineTaskList + '/?bid=' + baselineId);
    };

    const onEditBaseline = (baselineId: number) => {
        setBaselineId(baselineId);
        setIsBaselineDrawerOpen(true);
    };

    const onBaselineDrawerSave = () => {
        onDrawerClose();

        if (!updateSearchParams('pageNumber', 1)) search(searchParams);
    };

    useEffect(() => {
        // Update currentFormState whenever baselineId changes
        setCurrentFormState({ ...currentFormState, baselineId: baselineId });
        setFormData({ ...formData, baselineId: baselineId });
    }, [baselineId]);

    useEffect(() => {
        search(searchParams);
    }, [searchParams]);

    useEffect(() => {
        // load feature toggles & build columns on page load
        async function pageLoadFetch() {
            await loadFormFilters();
            //buildColumnOptions();
        }

        pageLoadFetch();
    }, []);

    useEffect(() => {
        buildColumnOptions();
    }, [formFilters]);

    const getStaticColumns = (): Array<BaselineTaskColumn> => {
        return [
            {
                field: 'baselineName',
                header: 'Task Name',
                isFeatureEnabled: true,
                default: true,
                isSortable: true,
                body: taskNameTemplate,
            },
            {
                field: 'startDaysOut',
                header: 'Relative Start',
                isFeatureEnabled: formFilters.isStartDateActive,
                body: startDaysOutTemplate,
                default: true,
                isSortable: true,
            },
            {
                field: 'targetDaysOut',
                header: 'Relative Target',
                isFeatureEnabled: true,
                body: targetDaysOutTemplate,
                default: true,
                isSortable: true,
            },
            {
                field: 'startStaticDate',
                header: 'Static Start',
                isFeatureEnabled: formFilters.isStartDateActive,
                body: startStaticDateTemplate,
                default: true,
                isSortable: true,
            },
            {
                field: 'targetStaticDate',
                header: 'Static Target',
                isFeatureEnabled: true,
                body: targetStaticDateTemplate,
                default: true,
                isSortable: true,
            },
            {
                field: 'baselineJobTitle',
                header: 'Job Title',
                isFeatureEnabled: true,
                default: true,
                isSortable: true,
                body: jobTitleTemplate,
            },
            {
                field: 'department',
                header: 'Department Name',
                isFeatureEnabled: true,
                default: true,
                isSortable: true,
                body: departmentTemplate,
            },
            {
                field: 'additionalInfo',
                header: 'Additional Information',
                isFeatureEnabled: true,
                default: true,
                body: additionalInfoTemplate,
                isSortable: true,
            },
            {
                field: 'anticipatedEffort',
                header: 'Anticipated Effort',
                isFeatureEnabled: formFilters.isTaskEffortActive,
                default: false,
                isSortable: true,
                body: anticipatedEffortTemplate,
            },
            {
                field: 'ordinal',
                header: 'Task Order',
                isFeatureEnabled: true,
                default: false,
                isSortable: true,
                body: ordinalTemplate,
            },
            {
                field: 'discipline',
                header: 'Discipline',
                isFeatureEnabled: true,
                isSortable: true,
                default: false,
                body: disciplineTemplate,
            },
            {
                field: 'community',
                header: 'Community',
                isFeatureEnabled: true,
                isSortable: true,
                default: false,
                body: communityTemplate,
            },
            {
                field: 'phase',
                header: 'Phase',
                isFeatureEnabled: true,
                isSortable: true,
                default: false,
                body: phaseTemplate,
            },
            {
                field: 'totalDependencies',
                header: '# of Dependencies',
                isFeatureEnabled: formFilters.isTaskDependenciesActive,
                isSortable: true,
                default: false,
                body: totalDependenciesTemplate,
            },
            {
                field: 'totalSubtasks',
                header: '# of Steps',
                isFeatureEnabled: formFilters.isTaskStepsActive,
                default: true,
                isSortable: true,
                body: totalSubtasksTemplate,
            },
            {
                field: 'createdDate',
                header: 'Created Date',
                isFeatureEnabled: true,
                default: false,
                isSortable: true,
                body: createdDateTemplate,
            },
            {
                field: 'modifiedDate',
                header: 'Last Modified',
                isFeatureEnabled: true,
                default: false,
                isSortable: true,
                body: modifiedDateTemplate,
            },
            {
                field: 'isActive',
                header: 'Active',
                isFeatureEnabled: true,
                body: activeTemplate,
                default: true,
                isSortable: true,
            },
            {
                field: 'links',
                header: 'Links',
                isFeatureEnabled: true,
                body: linksTemplate,
                default: false,
                isSortable: false,
            },
            {
                field: 'tags',
                header: 'Tags',
                isFeatureEnabled: true,
                default: false,
                isSortable: true,
                body: tagsTemplate,
            },
            {
                field: 'IsMilestone',
                header: 'Milestone',
                isFeatureEnabled: true,
                isSortable: true,
                default: false,
                body: (rowData: any) => milestoneTemplate(rowData) || null,
            },
            {
                field: 'includeCertificationCheckbox',
                header: 'Certification',
                isFeatureEnabled: true,
                isSortable: true,
                default: false,
                body: certificationTemplate,
            },
            {
                field: 'workflows',
                header: 'Workflows',
                isFeatureEnabled: true,
                isSortable: true,
                default: false,
                body: workflowsTemplate,
            },
        ];
    };

    const buildColumnOptions = () => {
        const columns = getStaticColumns().slice();
        const selectedColumns: Array<BaselineTaskColumn> = [];

        // Look for columns personalization, else use the default
        if (userColumns && userColumns.length) {
            for (const uc of userColumns) {
                const col = columns.find((c) => c.field === uc);
                if (col) {
                    selectedColumns.push(col);
                }
            }
        } else {
            for (const col of columns) {
                if (col.default === true) {
                    selectedColumns.push(col);
                }
            }
        }

        const columnOptions: Array<{ label: string; value: BaselineTaskColumn }> = [];
        for (const col of columns) {
            if (col.isFeatureEnabled) {
                columnOptions.push({ label: col.header, value: col });
            }
        }

        setColumns(selectedColumns);
        setColumnOptions(columnOptions);
    };

    const rowGroupHeaderTemplate = (data) => {
        const isLoadingTasks = expandedBaselineTasks.get(data.baselineId)?.loading ?? false;
        return (
            <React.Fragment>
                <div className='d-inline-flex justify-content-between fw-bold' style={{ width: '96%' }}>
                    <div className='p-2'>
                        {data.baselineName}
                        {isLoadingTasks && <i className='fa-regular fa-spinner fa-spin ms-2' />}
                    </div>
                    <RowGroupHeaderButtons>
                        <Button icon='far fa-pen-to-square' onClick={() => onEditBaseline(data.baselineId)} title='Edit Baseline' className='fa-lg' />
                        <Button
                            icon='far fa-arrow-up-right-from-square'
                            onClick={() => onExpandTasks(data.baselineId)}
                            title='Expand Tasks'
                            className='fa-lg'
                        />
                        <Button
                            icon='far fa-circle-plus'
                            onClick={async () => {
                                try {
                                    setFormData({ ...formData, baselineIdsSelected: [data.baselineId] });
                                    setIsCreateTaskDrawerOpen(true);
                                } catch (error) {
                                    console.error('Error while adding baseline task:', error);
                                }
                            }}
                            title='Add Baseline Task'
                            className='fa-lg'
                        />
                        <Button
                            icon='far fa-trash-can'
                            onClick={() => showUnlinkModal(data.baselineId)}
                            title='"Remove Baseline'
                            className='fa-lg trash-can'
                        />
                    </RowGroupHeaderButtons>
                </div>
            </React.Fragment>
        );
    };

    const rowGroupFooterTemplate = (data) => {
        const state = expandedBaselineTasks.get(data.baselineId);
        if (!state || state.loading || state.tasks.length >= state.totalCount) return null;

        return (
            <div style={{ width: '100%', textAlign: 'center', padding: '4px 0' }}>
                <Button
                    label={state.loadingMore ? 'Loading...' : `Load More (${state.tasks.length} of ${state.totalCount})`}
                    disabled={state.loadingMore}
                    onClick={() => loadMoreTasksForBaseline(data.baselineId, data.baselineName)}
                    className='p-button-text p-button-sm'
                    icon={state.loadingMore ? 'fa-regular fa-spinner fa-spin' : 'fa-regular fa-chevron-down'}
                    iconPos='right'
                />
            </div>
        );
    };

    const taskNameTemplate = (rowData) => {
        return <div className='show-newlines pointer-template'>{rowData.baselineTaskName}</div>;
    };

    const startDaysOutTemplate = (rowData) => {
        return <div className='pointer-template'>{daysToDaysWeeks(rowData.startDaysOut)}</div>;
    };

    const targetDaysOutTemplate = (rowData) => {
        return <div className='pointer-template'>{daysToDaysWeeks(rowData.targetDaysOut)}</div>;
    };

    const startStaticDateTemplate = (rowData) => {
        if (rowData.startStaticDate) {
            return Moment.utc(rowData.startStaticDate).format(DATE_FORMAT);
        }

        return '';
    };

    const targetStaticDateTemplate = (rowData) => {
        if (rowData.targetStaticDate) {
            return Moment.utc(rowData.targetStaticDate).format(DATE_FORMAT);
        }

        return null;
    };

    const additionalInfoTemplate = (rowData) => {
        return <div className='show-newlines pointer-template'>{TruncateText(rowData.additionalInfo, 200)}</div>;
    };

    const activeTemplate = (rowData) => {
        if (rowData.isActive) {
            return (
                <div className='text-success text-center pointer-template'>
                    <Icon name='check-circle' />
                </div>
            );
        } else {
            return (
                <div className='text-danger text-center'>
                    <Icon name='times-circle' />
                </div>
            );
        }
    };

    const createdDateTemplate = (rowData) => {
        return Moment.utc(rowData.createdDate).format(DATETIME_FORMAT);
    };

    const modifiedDateTemplate = (rowData) => {
        return Moment.utc(rowData.modifiedDate).format(DATETIME_FORMAT);
    };

    const linksTemplate = (rowData) => {
        const retval: React.JSX.Element[] = [];

        if (rowData.links && rowData.links.length > 0) {
            for (let i = 0; i < rowData.links.length; i++) {
                const link = rowData.links[i];

                if (link.customerFileId) {
                    retval.push(
                        <button key={link.ordinal} type='button' className='btn btn-link' onClick={(e) => onDownloadLink(link, e)}>
                            {link.name}
                        </button>
                    );
                } else {
                    let protocol = '';
                    const linkUrl = link.url.toLowerCase();
                    if (!linkUrl.startsWith('http')) protocol = 'http://';

                    retval.push(
                        <div key={link.ordinal}>
                            <a href={protocol + link.url} rel='noopener noreferrer' target='_blank'>
                                {link.name}
                            </a>
                        </div>
                    );
                }
            }
        }

        return retval;
    };

    const tagsTemplate = (rowData) => {
        const retval: React.JSX.Element[] = [];

        if (rowData.tags && rowData.tags.length > 0) {
            for (let i = 0; i < rowData.tags.length; i++) {
                const tag = rowData.tags[i];

                retval.push(
                    <div className='mb-1' key={`${tag.name}-${i}`}>
                        <Tag title={tag.name} color={tag.fontColor} backgroundColor={tag.backgroundColor} />
                    </div>
                );
            }
        }
        return retval;
    };

    const milestoneTemplate = (rowData) => {
        if (rowData.isMilestone) {
            return (
                <div className='text-warning milestone' title='Milestone Task'>
                    &#x25C6;
                </div>
            );
        }
    };

    const jobTitleTemplate = (rowData) => {
        return <div className='pointer-template'>{rowData.baselineJobTitle}</div>;
    };

    const departmentTemplate = (rowData) => {
        return <div className='pointer-template'>{rowData.department}</div>;
    };

    const communityTemplate = (rowData) => {
        return <div className='pointer-template'>{rowData.community}</div>;
    };

    const phaseTemplate = (rowData) => {
        return <div className='pointer-template'>{rowData.phase}</div>;
    };

    const disciplineTemplate = (rowData) => {
        return <div className='pointer-template'>{rowData.discipline}</div>;
    };

    const ordinalTemplate = (rowData) => {
        return <div className='pointer-template'>{rowData.ordinal}</div>;
    };

    const anticipatedEffortTemplate = (rowData) => {
        return <div className='pointer-template'>{rowData.anticipatedEffort}</div>;
    };

    const totalDependenciesTemplate = (rowData) => {
        return <div className='pointer-template'>{rowData.totalDependencies}</div>;
    };

    const totalSubtasksTemplate = (rowData) => {
        return <div className='pointer-template'>{rowData.totalSubtasks}</div>;
    };

    const certificationTemplate = (rowData) => {
        if (rowData.includeCertificationCheckbox === 1 || rowData.includeCertificationCheckbox === true) {
            return (
                <div className='text-success text-center pointer-template'>
                    <Icon name='check-circle' />
                </div>
            );
        } else {
            return <div className='pointer-template'></div>;
        }
    };

    const workflowsTemplate = (rowData) => {
        const workflowEventName = rowData.workflowEventName || '';

        if (!workflowEventName.trim()) {
            return <div className='pointer-template'></div>;
        }

        // Split by \r\n and create elements with <br> separators
        const workflowLines = workflowEventName.split(/\r\n|\n|\r/).filter(line => line.trim());

        return (
            <div className='pointer-template'>
                {workflowLines.map((line, index) => {
                    const colonIndex = line.indexOf(':');
                    if (colonIndex > -1) {
                        const eventName = line.substring(0, colonIndex).trim();
                        const afterColon = line.substring(colonIndex + 1).trim();
                        return (
                            <React.Fragment key={`workflow-line-${index}`}>
                                <span><strong>{eventName}:</strong> {afterColon}</span>
                                {index < workflowLines.length - 1 && <br />}
                            </React.Fragment>
                        );
                    } else {
                        return (
                            <React.Fragment key={`workflow-line-${index}`}>
                                <span>{line}</span>
                                {index < workflowLines.length - 1 && <br />}
                            </React.Fragment>
                        );
                    }
                })}
            </div>
        );
    };

    // Helper function to convert IDs to objects for MultiSelect value
    const getSelectedOptions = (selectedIds: Array<number>, options: Array<any>) => {
        if (!selectedIds || selectedIds.length === 0) return [];
        return options.filter((option) => selectedIds.includes(option.id));
    };

    const renderColumns = () => {
        const activeOptions = [
            { label: 'Yes', value: true },
            { label: 'No', value: false },
            { label: 'All', value: null },
        ];

        const filters = {
            baselineName: (
                <BaselineTaskNameFilter>
                    <MultiSelect
                        className='ui-column-filter'
                        optionLabel='name'
                        dataKey='id'
                        placeholder='Baseline'
                        value={getSelectedOptions(searchParams.baselines, formFilters.baselineIdsFilter)}
                        options={formFilters.baselineIdsFilter}
                        onChange={(e) => onCustomMultiSelectChange('baselines', e.value)}
                        maxSelectedLabels={1}
                    />
                    <input
                        type='text'
                        name='baselineTaskName'
                        className='p-inputtext'
                        size={10}
                        onBlur={onSearchBlur}
                        onKeyUp={keyUpFilter}
                        placeholder='Task Name'
                    />
                </BaselineTaskNameFilter>
            ),
            additionalInfo: (
                <input
                    type='text'
                    name='additionalInfo'
                    className='p-inputtext'
                    size={10}
                    onBlur={onSearchBlur}
                    onKeyUp={keyUpFilter}
                    placeholder='Additional Info'
                />
            ),
            department: (
                <MultiSelect
                    className='ui-column-filter'
                    optionLabel='name'
                    dataKey='id'
                    value={getSelectedOptions(searchParams.departments, formFilters.departments)}
                    options={formFilters.departments}
                    onChange={(e) => onCustomMultiSelectChange('departments', e.value)}
                    maxSelectedLabels={1}
                />
            ),
            baselineJobTitle: (
                <MultiSelect
                    className='ui-column-filter'
                    optionLabel='name'
                    dataKey='id'
                    value={getSelectedOptions(searchParams.baselineJobTitles, formFilters.baselineJobTitles)}
                    options={formFilters.baselineJobTitles}
                    onChange={(e) => onCustomMultiSelectChange('baselineJobTitles', e.value)}
                    maxSelectedLabels={1}
                />
            ),
            community: (
                <MultiSelect
                    className='ui-column-filter'
                    optionLabel='name'
                    dataKey='id'
                    value={getSelectedOptions(searchParams.communities, formFilters.communities)}
                    options={formFilters.communities}
                    onChange={(e) => onCustomMultiSelectChange('communities', e.value)}
                    maxSelectedLabels={1}
                />
            ),
            phase: (
                <MultiSelect
                    className='ui-column-filter'
                    optionLabel='name'
                    dataKey='id'
                    value={getSelectedOptions(searchParams.phases, formFilters.phases)}
                    options={formFilters.phases}
                    onChange={(e) => onCustomMultiSelectChange('phases', e.value)}
                    maxSelectedLabels={1}
                />
            ),
            discipline: (
                <MultiSelect
                    className='ui-column-filter'
                    optionLabel='name'
                    dataKey='id'
                    value={getSelectedOptions(searchParams.disciplines, formFilters.disciplines)}
                    options={formFilters.disciplines}
                    onChange={(e) => onCustomMultiSelectChange('disciplines', e.value)}
                    maxSelectedLabels={1}
                />
            ),
            isActive: <Dropdown className='ui-column-filter' value={searchParams.isActive} options={activeOptions} onChange={onActiveChange} />,
            createdDate: (
                <Button
                    type='button'
                    className='light ui-column-filter'
                    name='createdDateRangeButton'
                    label={searchParams.createdDateStart || searchParams.createdDateEnd ? 'Edit Range' : 'Range'}
                    onClick={() => showDateRangePicker('createdDate')}
                />
            ),
            tags: (
                <MultiSelect
                    className='ui-column-filter'
                    optionLabel='name'
                    placeholder='Filter'
                    dataKey='id'
                    value={getSelectedOptions(searchParams.tags, formFilters.taskTags)}
                    options={formFilters.taskTags}
                    onChange={(e) => onCustomMultiSelectChange('tags', e.value)}
                    maxSelectedLabels={1}
                />
            ),
        };

        const cols = columns.map((col) => {
            const filterElement = filters[col.field];
            // HACK: (jwv) PrimeReact's MultiSelect is throwing an error any time we deselect a column from the column selector
            // when it's internal object comparison code tries to set the _$visible property on a style object defined in our
            // columns objects.  IsActive was the only column with style object, and it was setting the width to 80px, so this
            // hack is in place to explicitly set that on the column here, instead of definining the style object as a member
            // of the column definition.
            if (col.field === 'isActive') {
                return (
                    <Column
                        key={col.field}
                        sortable={col.isSortable}
                        field={col.field}
                        filter={true}
                        filterElement={filterElement}
                        header={col.header}
                        body={col.body}
                        style={{ width: '80px' }}
                    />
                );
            } else if (filterElement && col.isFeatureEnabled) {
                return (
                    <Column
                        key={col.field}
                        sortable={col.isSortable}
                        field={col.field}
                        filter={true}
                        filterElement={filterElement}
                        header={col.header}
                        body={col.body}
                        style={col.style}
                    />
                );
            } else if (col.isFeatureEnabled) {
                return <Column key={col.field} sortable={col.isSortable} field={col.field} header={col.header} body={col.body} />;
            } else {
                return null;
            }
        });

        return cols;
    };

    const onDownloadLink = async (link, e) => {
        e.preventDefault();
        try {
            // extract the file name from the link.url
            const start = link.url.indexOf('=') + 1;
            const fileName = link.url.substr(start, link.url.length - start);

            await downloadFile(fileName);
        } catch (error) {
            Notification('error', 'Error', error);
        }
    };

    const onSearchBlur = (e) => {
        updateSearchParams(e.target.name, e.target.value);
    };

    const onActiveChange = (e) => {
        const selectedValue = getNullableOnChangeValue(e);
        updateSearchParams('isActive', selectedValue);
    };

    const onCustomMultiSelectChange = (name: string, selectedValues: any) => {
        updateSearchParams(
            name,
            (selectedValues || []).map((item) => item.id).filter((id) => id !== null && id !== undefined)
        );
    };

    const showDateRangePicker = (rangeName) => {
        const start = searchParams.createdDateStart ? searchParams.createdDateStart : null;
        const end = searchParams.createdDateEnd ? searchParams.createdDateEnd : null;

        const newDateRange = {
            ...datePickerRange,
            name: rangeName,
        };

        if (typeof end === 'string') {
            newDateRange.end = new Date(Date.parse(end));
        }
        if (typeof start === 'string') {
            newDateRange.start = new Date(Date.parse(start));
        }

        setDatePickerRange(newDateRange);
    };

    const onOptionsChange = (selectedOption: { value: number }) => {
        switch (selectedOption.value) {
            case 1:
                importTasksModalRef.current?.show();
                break;
            case 2:
                setIsCreateTaskDrawerOpen(true);
                break;
            case 3:
                copyBaselineModalRef.current?.show();
                break;
            case 4:
            default:
                newBaselineModalRef.current?.show();
                break;
        }
    };

    const onSort = ({ sortField, sortOrder }) => {
        // Step 1: Group data by baselineName
        const groupedData = baselines.reduce((acc, item) => {
            if (!acc[item.baselineName]) {
                acc[item.baselineName] = [];
            }
            acc[item.baselineName].push(item);
            return acc;
        }, {});

        // Step 2: Sort items inside each group
        Object.keys(groupedData).forEach((group) => {
            groupedData[group].sort((a, b) => {
                const valueA = a[sortField] ?? '';
                const valueB = b[sortField] ?? '';

                if (typeof valueA === 'string' && typeof valueB === 'string') {
                    return valueA.localeCompare(valueB) * sortOrder;
                } else {
                    return (valueA - valueB) * sortOrder;
                }
            });
        });

        // Step 3: Reconstruct sorted array while maintaining group order
        const sortedData = Object.entries(groupedData).flatMap(([_, group]) => group);

        // Step 4: Update DataTable state
        setBaselines(sortedData);
    };

    const showUnlinkModal = async (baselineId: number) => {
        setBaselineId(baselineId);
        removeBaselineModalRef.current?.show();
    };

    const onRemoveBaseline = async (baselineId: number) => {
        removeBaselineModalRef.current?.hide();

        // De-link the baseline from the project
        setLoading((prevLoading) => prevLoading + 1);
        try {
            const request = { baselineId, projectId: projectData.id };
            const result = await unlinkBaseline(request);

            if (result.type === types.UNLINK_BASELINE_SUCCESS) {
                // Refresh the list
                if (!updateSearchParams('pageNumber', 1)) await search(searchParams);
            } else if (result.type === types.UNLINK_BASELINE_FAILURE) {
                Notification('error', 'An Error Occurred', result.payload.message);
            }
        } catch (error) {
            Notification('error', 'Error', error);
        } finally {
            setLoading((prevLoading) => prevLoading - 1);
        }
    };

    const onModalOperationCompletes = async (modalRef: React.MutableRefObject<ModalRefProps | null>) => {
        modalRef.current?.hide();

        await Promise.all([loadFormFilters(true), search(searchParams)]);
    };

    const showInactivateModal = async () => {
        setBaselineTaskInactivateModal({
            baselineTaskIds: selectedBaselineTasks.map((task) => task.baselineTaskId),
            isActive: false,
            isLocationTaskListActive: false,
        });

        inactivateBaselineTasks.current?.show();
    };

    const closeInactivateModal = useCallback(() => {
        // Reset modal state
        setBaselineTaskInactivateModal({
            baselineTaskIds: [],
            isActive: false,
            isLocationTaskListActive: false,
        });

        // Reset local radio state
        setLocalRadioState({
            shouldRemoveFromBaseline: true, // Default to Yes (most common)
            shouldRemoveFromLocations: true, // Default to Yes (most common)
        });

        // Hide the modal
        inactivateBaselineTasks.current?.hide();
    }, []);

    // Optimized radio button handlers
    const handleRemoveFromBaselineChange = useCallback(
        (shouldRemove: boolean) => {
            if (shouldRemove) {
                // User selected "Yes" - update local state only
                setLocalRadioState((prev) => ({
                    ...prev,
                    shouldRemoveFromBaseline: true,
                }));
            } else {
                // User selected "No" - close modal immediately
                closeInactivateModal();
            }
        },
        [closeInactivateModal]
    );

    const handleRemoveFromLocationsChange = useCallback((shouldRemove: boolean) => {
        setLocalRadioState((prev) => ({
            ...prev,
            shouldRemoveFromLocations: shouldRemove,
        }));
    }, []);

    const onSaveInactiveBaselineTasks = async () => {
        // Convert local radio state to the format expected by the API
        const apiPayload = {
            baselineTaskIds: baselineTaskInactivateModal.baselineTaskIds,
            isActive: !localRadioState.shouldRemoveFromBaseline, // Flip the logic for API
            isLocationTaskListActive: !localRadioState.shouldRemoveFromLocations, // Flip the logic for API
        };

        setLoading((prevLoading) => prevLoading + 1);
        try {
            const result = await baselineTasksActiveToggle(apiPayload);

            if (result.type === types.TOGGLE_ACTIVE_TASK_SUCCESS) {
                if (!updateSearchParams('pageNumber', 1)) await search(searchParams);
                closeInactivateModal();
            } else if (result.type === types.TOGGLE_ACTIVE_TASK_FAILURE) {
                Notification('error', 'An Error Occurred', result.payload.message);
                closeInactivateModal();
            }
        } catch (error) {
            Notification('error', 'Error', error);
            closeInactivateModal();
        } finally {
            setLoading((prevLoading) => prevLoading - 1);
        }
    };

    const onOrderTasksComplete = () => {
        setIsOrderTasksDrawerOpen(false);

        if (!updateSearchParams('pageNumber', 1)) search(searchParams);
    };

    const onOrderTasksCancel = () => {
        setIsOrderTasksDrawerOpen(false);
    };

    const onBulkUpdateAttributesComplete = () => {
        setIsBulkUpdateAttributesDrawerOpen(false);

        if (!updateSearchParams('pageNumber', 1)) search(searchParams);
    };

    const onBulkUpdateAttributesCancel = () => {
        setIsBulkUpdateAttributesDrawerOpen(false);
    };

    const onBulkPushdownComplete = () => {
        setIsBulkPushdownDrawerOpen(false);
        setSelectedBaselineTasks([]);
        
        if (!updateSearchParams('pageNumber', 1)) search(searchParams);
    };

    const onBulkPushdownCancel = () => {
        setIsBulkPushdownDrawerOpen(false);
    };

    const handleRowClick = async (rowData) => {
        setBaselineId(rowData.baselineId);

        try {
            // Fetch full task data from the backend
            const response = await getBaselineTask(rowData.baselineTaskId);

            if (response.type === types.LOAD_BASELINE_TASK_SUCCESS) {
                const task = response.payload;

                // Map and wait for form data
                const mapped = await mapToFormData(task, rowData.baselineId);

                // Update state before showing modal
                setFormData(mapped);
                setCurrentFormState({
                    ...currentFormState,
                    editTask: mapped,
                    unchangedTask: mapped,
                    newTask: mapped,
                    baselineId: rowData.baselineId,
                });

                setTaskFormMode('update');

                setIsCreateTaskDrawerOpen(true);
            }
        } catch (error) {
            console.error('Error fetching task data:', error);
        }
    };

    const calculateWeeksAndDays = (daysOut, isStaticDate) => {
        if (daysOut != null && !isStaticDate) {
            const value = parseInt(daysOut.toString());
            return {
                days: (value % 7),
                weeks: Math.floor(value / 7),
            };
        }
        return { days: null, weeks: null };
    };

    const mapToFormData = async (task, baselineId) => {
        const baselineIdsSelected =
            typeof task.baselineIdsSelected === 'string'
                ? task.baselineIdsSelected.split(',').map(Number)
                : Array.isArray(task.baselineIdsSelected)
                    ? task.baselineIdsSelected
                    : [baselineId];

        const showStaticDates = Boolean(task.startStaticDate || task.targetStaticDate);

        const { days: startDays, weeks: startWeeks } = calculateWeeksAndDays(task.startDaysOut, task.startStaticDate);
        const { days: targetDays, weeks: targetWeeks } = calculateWeeksAndDays(task.targetDaysOut, task.targetStaticDate);

        return {
            id: task.id ?? 0,
            name: task.name ?? '',
            description: task.additionalInfo ?? '',
            contingentId: task.contingentId ?? null,
            phaseId: task.phaseId ?? null,
            departmentId: task.departmentId ?? null,
            jobTitleId: task.jobTitleId ?? null,
            contingentName: task.contingentName ?? null,
            baselineId,
            contactName: task.contactName ?? null,
            contactEmail: task.contactEmail ?? null,
            additionalInfo: task.additionalInfo ?? '',
            isActive: task.isActive ?? true,
            isLocationTaskListActive: true,
            links: Array.isArray(task.links) ? task.links.map((link) => ({ ...link })) : [],
            targetDaysOut: task.targetDaysOut ?? null,
            targetDays,
            targetWeeks,
            targetStaticDate: task.targetStaticDate ?? null,
            startDaysOut: task.startDaysOut ?? null,
            startDays,
            startWeeks,
            startStaticDate: task.startStaticDate ?? null,
            anticipatedEffort: task.anticipatedEffort ?? null,
            steps: Array.isArray(task.steps) ? task.steps.map((step) => ({ ...step, ordinal: step.ordinal?.toString() })) : [],
            prereqTasks: Array.isArray(task.prereqTasks) ? task.prereqTasks.map((t) => ({ ...t })) : [],
            showStaticDates,
            isMilestone: task.isMilestone ?? false,
            baselineIds: [],
            baselineIdsSelected,
            communityId: task.communityId ?? null,
            disciplineId: formFilters.disciplines.find((d) => d.name === task.discipline)?.id ?? null,
            ordinal: task.ordinal ?? 0,
            taskTagIds: task.taskTagIds ?? [],
            includeCertificationCheckbox: task.includeCertificationCheckbox ?? false,
            workflows: Array.isArray(task.workflows) ? task.workflows.map((w) => ({ ...w })) : [],
            workflowEventId: task.workflowEventId ?? null,
            isLocationTaskDateAdjustable: task.isLocationTaskDateAdjustable ?? true,
            createdByName: task.createdByName ?? '',
            createdDate: task.createdDate ?? '',
            modifiedByName: task.modifiedByName ?? '',
            modifiedDate: task.modifiedDate ?? ''
        } as Task;
    };

    return (
        <div className='publish-content'>
            <div className='d-flex justify-content-end'>
                <ButtonBaselineTabs>
                    <button
                        type='button'
                        className='pacer61-primary-purple-btn my-3 rounded-left'
                        onClick={async () => {
                            try {
                                // Reset form data to start with empty task
                                resetFormData(baselineId);

                                // Reset form state to initial values
                                setCurrentFormState({
                                    ...currentFormState,
                                    editTask: null,
                                    unchangedTask: null,
                                    newTask: null,
                                    isFormValid: true,
                                    showDateError: false,
                                    unsavedChanges: false,
                                });

                                // Set task form mode to create
                                setTaskFormMode('create');

                                // Open the modal
                                setIsCreateTaskDrawerOpen(true);
                            } catch (error) {
                                console.error('Error while adding baseline task:', error);
                            }
                        }}>
                        <i className='fa-regular fa-share-from-square'></i>
                        <span className='mx-2'>Add</span>
                    </button>
                </ButtonBaselineTabs>
                <DropdownBaselineTabs>
                    <Dropdown
                        name='isActive'
                        className='dropdown-baseline-tabs'
                        style={{
                            textAlign: 'left',
                            width: '35px',
                            backgroundColor: '#6860e4',
                            color: 'white',
                            fontFamily: 'Greycliff',
                            fontWeight: '400',
                            fontSize: '12px',
                        }}
                        options={activeOptions}
                        itemTemplate={optionTemplate}
                        onChange={onOptionsChange}
                    />
                </DropdownBaselineTabs>
            </div>

            <DataTable
                value={baselines}
                rowGroupMode='subheader'
                groupRowsBy='baselineName'
                sortMode='single'
                sortField={sortField}
                sortOrder={(sortOrder as any) || 1}
                expandableRowGroups
                expandedRows={expandedRows}
                onRowToggle={handleRowToggle}
                rowGroupHeaderTemplate={rowGroupHeaderTemplate}
                rowGroupFooterTemplate={rowGroupFooterTemplate}
                rowClassName={(row: any) => ({ 'd-none': row._placeholder })}
                header={headerComponent}
                className='p-datatable-sm p-datatable-striped'
                loading={loading !== 0}
                filterDisplay='row'
                reorderableColumns
                onSort={onSort}
                onColReorder={(e) => onColumnReorder(e)}
                onRowClick={(e: any) => { if (!e.data._placeholder) handleRowClick(e.data); }}
                selectionMode='checkbox'
                selection={selectedBaselineTasks}
                onSelectionChange={onSelectionChange}
                dataKey='baselineTaskId'
                showSelectAll={false}>
                <Column selectionMode='multiple' headerStyle={{ width: '3rem' }} />
                {renderColumns()}
            </DataTable>

            <Paginator
                leftContent={<span>Rows per page:</span>}
                rows={searchParams.pageSize}
                totalRecords={totalCount}
                first={(searchParams.pageNumber - 1) * searchParams.pageSize}
                rowsPerPageOptions={[5, 10, 20, 50, 100]}
                onPageChange={onPageChange}
                template='RowsPerPageDropdown CurrentPageReport FirstPageLink PrevPageLink NextPageLink LastPageLink'
                currentPageReportTemplate='{first}-{last} of {totalRecords}'
            />

            {isColumnDrawerOpen && (
                <BaselineTaskListColumnDrawer
                    onComplete={onDrawerClose}
                    onColumnToggle={onColumnToggle}
                    columnOptions={columnOptions}
                    columns={columns}
                />
            )}

            {isOrderTasksDrawerOpen && (
                <OrderTasksDrawer
                    subscriptionId={subscriptionId.toString()}
                    baselineId={baselineId.toString()}
                    targetDaysOut={orderTasksTargetDaysOut}
                    targetStaticDate={orderTasksTargetStaticDate}
                    onCancel={onOrderTasksCancel}
                    onComplete={onOrderTasksComplete}
                />
            )}
            {isBulkUpdateAttributesDrawerOpen && (
                <BulkUpdateAttributesDrawer
                    baselineId={baselineId.toString()}
                    checkedTasks={selectedBaselineTasks.map(({ baselineTaskId, ...rest }) => ({
                        id: baselineTaskId,
                        ...rest,
                    }))}
                    onComplete={onBulkUpdateAttributesComplete}
                    onCancel={onBulkUpdateAttributesCancel}
                />
            )}

            {isBulkPushdownDrawerOpen && (
                <BulkPushdownDrawer
                    baselineId={baselineId.toString()}
                    projectId={projectData.id}
                    checkedTasks={selectedBaselineTasks.map(({ baselineTaskId, ...rest }) => ({
                        id: baselineTaskId,
                        ...rest,
                    }))}
                    onComplete={onBulkPushdownComplete}
                    onCancel={onBulkPushdownCancel}
                    baselineTaskActions={baselineTaskActionCreators}
                />
            )}

            {isBaselineDrawerOpen && (
                <BaselinesForm
                    cancelClick={onDrawerClose}
                    saveClick={onBaselineDrawerSave}
                    baselineId={baselineId}
                    subscriptionId={subscriptionId.toString()}
                />
            )}
            {isCreateTaskDrawerOpen && (
                <CreateTaskForm
                    taskFormMode={taskFormMode}
                    saveClick={onSave}
                    cancelClick={() => {
                        setIsCreateTaskDrawerOpen(false);
                    }}
                    currentTask={formData}
                    isFormValid={currentFormState.isFormValid}
                    onFormChange={onFormChange}
                    checkForChanges={checkForChanges}
                    formFilters={formFilters}
                    baselineId={baselineId}
                    showDateError={currentFormState.showDateError}
                    isFromProject={true}
                />
            )}
            <Modal61
                ref={importTasksModalRef}
                title='Import Tasks'
                body={
                    <ImportTasksWizzard
                        projectData={projectData}
                        onModalOperationCompletes={() => {
                            onModalOperationCompletes(importTasksModalRef);
                        }}
                        formFilters={formFilters}
                    />
                }
                customFooter={
                    <div>
                        <button type='button' className={'btn btn-primary'} data-dismiss='modal' onClick={() => importTasksModalRef.current?.hide()}>
                            Cancel
                        </button>
                    </div>
                }
                onPrimaryClick={undefined}
                onSecondaryClick={undefined}
                onCloseClick={() => importTasksModalRef.current?.hide()}
                primaryButtonText={''}
                isLoading={false}
            />

            <Modal61
                ref={copyBaselineModalRef}
                title='Copy Baselines'
                body={
                    <CopyBaselineFromProject
                        ref={modalBodyRef}
                        subscriptionId={subscriptionId}
                        projectData={projectData}
                        onCopyBaselineComplete={() => {
                            onModalOperationCompletes(copyBaselineModalRef);
                        }}
                    />
                }
                primaryButtonText='Copy Baselines'
                secondaryButtonText='Cancel'
                onPrimaryClick={() => {
                    if (modalBodyRef.current) {
                        modalBodyRef.current.copyBaselines();
                    }
                }}
                onSecondaryClick={() => copyBaselineModalRef.current?.hide()}
                onCloseClick={() => copyBaselineModalRef.current?.hide()}
                isLoading={false}
            />

            <Modal61
                ref={newBaselineModalRef}
                title='New Baseline'
                body={
                    <CreateBaseline
                        ref={modalBodyRef}
                        subscriptionId={subscriptionId}
                        projectKey={projectData.projectKey}
                        onBaselineCreationComplete={() => {
                            onModalOperationCompletes(newBaselineModalRef);
                        }}
                    />
                }
                primaryButtonText='Save Baseline'
                secondaryButtonText='Cancel'
                onPrimaryClick={() => {
                    if (modalBodyRef.current) {
                        modalBodyRef.current.createBaseline();
                    }
                }}
                onSecondaryClick={() => newBaselineModalRef.current?.hide()}
                onCloseClick={() => newBaselineModalRef.current?.hide()}
                isLoading={false}
            />

            <Modal61
                ref={removeBaselineModalRef}
                title='Are you sure?'
                body={
                    <p>
                        Do you want to remove this baseline and tasks from the project? These tasks will not be included when you publish this project
                        in the future. If these tasks were published to location or teams already, those tasks will not be affected.
                    </p>
                }
                primaryButtonText='Yes'
                onPrimaryClick={() => onRemoveBaseline(baselineId)}
                showSecondaryButton={true}
                onSecondaryClick={undefined}
                onCloseClick={undefined}
                isLoading={false}
                isCentered={false}
            />
            <Modal61
                ref={inactivateBaselineTasks}
                title='Inactivate Tasks'
                body={
                    <>
                        <p>Are you sure you want to remove the selected tasks from the baseline?</p>
                        <RadioButtonOptions>
                            <div>
                                <RadioButton
                                    inputId='yes-remove-task'
                                    name='removeTask'
                                    value={true}
                                    onChange={() => handleRemoveFromBaselineChange(true)}
                                    checked={localRadioState.shouldRemoveFromBaseline === true}
                                />
                                <label htmlFor='yes-remove-task'>Yes</label>
                            </div>
                            <div>
                                <RadioButton
                                    inputId='no-keep-task'
                                    name='removeTask'
                                    value={false}
                                    onChange={() => handleRemoveFromBaselineChange(false)}
                                    checked={localRadioState.shouldRemoveFromBaseline === false}
                                />
                                <label htmlFor='no-keep-task'>No</label>
                            </div>
                        </RadioButtonOptions>
                        {/* Show the second radio group if user selected "Yes" to remove from baseline */}
                        {localRadioState.shouldRemoveFromBaseline && (
                            <>
                                <ModalTitleLayout>Should the selected tasks also be removed from locations/teams?</ModalTitleLayout>
                                <ModalSubtitleLayout>
                                    If you wish to phase-out these tasks and have existing locations/teams continue to complete the task, select No.
                                </ModalSubtitleLayout>
                                <RadioButtonOptions>
                                    <div>
                                        <RadioButton
                                            inputId='yes-remove-locations'
                                            name='removeFromLocations'
                                            value={true}
                                            onChange={() => handleRemoveFromLocationsChange(true)}
                                            checked={localRadioState.shouldRemoveFromLocations === true}
                                        />
                                        <label htmlFor='yes-remove-locations'>Yes, remove</label>
                                    </div>
                                    <div>
                                        <RadioButton
                                            inputId='no-keep-locations'
                                            name='removeFromLocations'
                                            value={false}
                                            onChange={() => handleRemoveFromLocationsChange(false)}
                                            checked={localRadioState.shouldRemoveFromLocations === false}
                                        />
                                        <label htmlFor='no-keep-locations'>No, keep visible at existing locations</label>
                                    </div>
                                </RadioButtonOptions>
                            </>
                        )}
                    </>
                }
                primaryButtonText='Save'
                isLoading={false}
                onPrimaryClick={onSaveInactiveBaselineTasks}
                showSecondaryButton={true}
                onSecondaryClick={closeInactivateModal}
            />
        </div>
    );
};

export default ProjectTasks;
